import { useCallback, useEffect } from 'react';
import { useTheme } from './use-theme';
import { useAuth } from '@/features/Auth/state/AuthContext';
import { useUserSettingsByUserId, useUpdateUserSettingsByUserId, useCreateUserSettings } from './api/useUserSettings';

/**
 * Sincroniza o tema com a preferência da CONTA (`user_settings.theme`).
 *
 * A preferência do DISPOSITIVO vive em localStorage e é aplicada antes daqui,
 * na inicialização do ThemeProvider — ver `shared/utils/themeStorage.ts`. Este
 * hook entra depois, quando a sessão resolve, e o banco prevalece para que a
 * escolha siga o usuário entre máquinas.
 *
 * Como o `setTheme` do provider já persiste no localStorage, tanto o toggle
 * quanto a carga vinda do banco atualizam os dois lados sem trabalho extra.
 */
export const useProfileTheme = () => {
  const { theme, setTheme } = useTheme();
  const { user } = useAuth();
  
  const { data: userSettings } = useUserSettingsByUserId(user?.id);
  const { mutate: updateUserSettings } = useUpdateUserSettingsByUserId();
  const { mutate: createUserSettings } = useCreateUserSettings();

  const saveThemePreference = useCallback(async (newTheme: string) => {
    if (!user?.id) return;

    try {
      if (userSettings) {
        // Atualizar as configurações existentes
        updateUserSettings({
          userId: user.id,
          data: { theme: newTheme }
        });
      } else {
        // Criar novas configurações
        createUserSettings({
          theme: newTheme
        });
      }
    } catch (error) {
      console.error('Erro ao salvar preferência de tema:', error);
    }
  }, [user, userSettings, updateUserSettings, createUserSettings]);

  const toggleTheme = useCallback(() => {
    const newTheme = theme === 'light' ? 'dark' : 'light';
    setTheme(newTheme);
    saveThemePreference(newTheme);
  }, [theme, setTheme, saveThemePreference]);

  useEffect(() => {
    // Carregar preferência de tema quando os dados do usuário ou configurações mudarem
    if (userSettings?.theme) {
      setTheme(userSettings.theme as 'dark' | 'light');
    }
  }, [userSettings, setTheme]);

  return { theme, toggleTheme };
};
